// ============================================================================
// Dienstplanung — Service Layer (Enterprise Grade)
//
// Supabase-First: DB-Tabellen week_schedules + shift_slots
// Fallback: In-Memory Demo für Offline/Dev
// Realtime: subscribeScheduleChanges() für Live-Updates
// ============================================================================

import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import type {
  WeekSchedule,
  ShiftSlot,
  ScheduleStaffMember,
  StaffRole,
  PositionConfig,
  SchedulePublishOptions,
  StaffAvailability,
  ScheduleSwapRequest,
  ArbZGViolation,
  HoursSummaryRow,
  StaffContractHours,
} from '@/types/schedule';
import { DEFAULT_POSITIONS } from '@/types/schedule';

// ============================================================================
// Helpers
// ============================================================================

let arbzgRpcUnavailable = false;
let hoursSummaryRpcUnavailable = false;

function isNonRecoverableScheduleRpcError(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  return error.code === 'PGRST202'
    || error.code === '42804'
    || error.code === '42883'
    || (error.message ?? '').includes('function public.')
    || (error.message ?? '').includes('COALESCE types');
}

/** ISO-Datum des Montags der Woche, die `date` enthält */
export function getWeekStart(date: Date): string {
  const d = new Date(date);
  const day = d.getDay();             // 0=So, 1=Mo, ...
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  // Lokales Datum formatieren (kein UTC-Offset-Problem)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;           // "2026-02-24"
}

/** KW-Nummer nach ISO 8601 */
export function getISOWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

/** Gibt die 5 Wochentage (Mo–Fr) ab einem Montag zurück */
export function getWeekDates(weekStart: string): Date[] {
  const start = new Date(weekStart + 'T00:00:00');
  return Array.from({ length: 5 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
}

/** Formatiert ein Datum als "Mo 24." */
export function formatDayShort(date: Date): string {
  const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  return `${days[date.getDay()]} ${date.getDate()}.`;
}

/** Formatiert ein Datum als lokales ISO-Datum ohne UTC-Drift */
export function formatISODateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Formatiert ein lokales Datum stabil als YYYY-MM-DD ohne UTC-Drift. */
export function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// ============================================================================
// Demo-Daten
// ============================================================================

const DEMO_STAFF: ScheduleStaffMember[] = [
  { id: 's1', name: 'Dr. Weber',     role: 'arzt',    color: 'bg-red-100',     is_available: true },
  { id: 's2', name: 'Dr. Klein',     role: 'arzt',    color: 'bg-red-100',     is_available: true },
  { id: 's3', name: 'Dr. Lang',      role: 'arzt',    color: 'bg-red-100',     is_available: true },
  { id: 's4', name: 'MFA Müller',    role: 'mfa',     color: 'bg-blue-100',    is_available: true },
  { id: 's5', name: 'MFA Schmidt',   role: 'mfa',     color: 'bg-blue-100',    is_available: true },
  { id: 's6', name: 'Fr. Paul',      role: 'empfang', color: 'bg-yellow-100',  is_available: true },
  { id: 's7', name: 'Hr. Richter',   role: 'empfang', color: 'bg-yellow-100',  is_available: true },
  { id: 's8', name: 'MFA Braun',     role: 'labor',   color: 'bg-emerald-100', is_available: true },
];

/**
 * Generiert Demo-Schichten. Nutzt echte Staff-Daten wenn vorhanden,
 * sonst Fallback auf DEMO_STAFF-Zuordnungen.
 */
function generateDemoSlots(
  weekStart: string,
  realStaff?: ScheduleStaffMember[],
): ShiftSlot[] {
  const dates = getWeekDates(weekStart);
  const scheduleId = 'demo-schedule';
  const practiceId = 'demo-practice';
  const now = new Date().toISOString();

  const d0 = dates[0]?.toISOString().slice(0, 10) ?? weekStart;
  const d2 = dates[2]?.toISOString().slice(0, 10) ?? weekStart;
  const d3 = dates[3]?.toISOString().slice(0, 10) ?? weekStart;
  const d4 = dates[4]?.toISOString().slice(0, 10) ?? weekStart;

  // Wenn echte Mitarbeiter vorhanden → daraus generieren
  if (realStaff && realStaff.length > 0) {
    return generateSlotsFromRealStaff(realStaff, {
      scheduleId, practiceId, now, d0, d2, d3, d4,
    });
  }

  // Fallback: Hardcoded Demo (nur wenn kein Supabase)
  return [
    mkSlot(scheduleId, practiceId, 's1', 'Dr. Weber', 'arzt', 'sprechstunde', d0, d2, false, now),
    mkSlot(scheduleId, practiceId, 's2', 'Dr. Klein', 'arzt', 'sprechstunde', d3, d4, false, now),
    mkSlot(scheduleId, practiceId, 's8', 'MFA Braun', 'labor', 'labor', d0, d4, false, now),
    mkSlot(scheduleId, practiceId, 's6', 'Fr. Paul', 'empfang', 'empfang', d0, d2, false, now),
    mkSlot(scheduleId, practiceId, 's7', 'Hr. Richter', 'empfang', 'empfang', d3, d4, false, now),
    mkSlot(scheduleId, practiceId, 's3', 'Dr. Lang', 'vertretung', 'vertretung', d2, d3, true, now),
    // Open Shifts (Demo)
    mkSlot(scheduleId, practiceId, null, null, 'mfa', 'empfang', d3, d3, false, now, true),
    mkSlot(scheduleId, practiceId, null, null, 'arzt', 'sprechstunde', d4, d4, false, now, true),
  ];
}

/** Helper: Slot-Objekt erzeugen */
function mkSlot(
  scheduleId: string, practiceId: string,
  staffId: string | null, staffName: string | null, staffRole: StaffRole,
  positionId: string, startDate: string, endDate: string,
  isOverride: boolean, now: string,
  isOpen = false,
): ShiftSlot {
  return {
    id: crypto.randomUUID(), schedule_id: scheduleId, practice_id: practiceId,
    staff_id: staffId ?? '', staff_name: staffName ?? '', staff_role: staffRole,
    position_id: positionId, start_date: startDate, end_date: endDate,
    is_override: isOverride, is_open: isOpen, created_at: now, updated_at: now,
  };
}

/** Generiere sinnvollen Demo-Dienstplan aus echten Mitarbeitern */
function generateSlotsFromRealStaff(
  staff: ScheduleStaffMember[],
  ctx: { scheduleId: string; practiceId: string; now: string; d0: string; d2: string; d3: string; d4: string },
): ShiftSlot[] {
  const slots: ShiftSlot[] = [];
  const byRole = (r: StaffRole) => staff.filter(s => s.role === r);

  // Ärzte → Sprechstunde (aufgeteilt auf Wochenhälften)
  const aerzte = byRole('arzt');
  if (aerzte.length >= 2) {
    slots.push(mkSlot(ctx.scheduleId, ctx.practiceId, aerzte[0]!.id, aerzte[0]!.name, 'arzt', 'sprechstunde', ctx.d0, ctx.d2, false, ctx.now));
    slots.push(mkSlot(ctx.scheduleId, ctx.practiceId, aerzte[1]!.id, aerzte[1]!.name, 'arzt', 'sprechstunde', ctx.d3, ctx.d4, false, ctx.now));
  } else if (aerzte.length === 1) {
    slots.push(mkSlot(ctx.scheduleId, ctx.practiceId, aerzte[0]!.id, aerzte[0]!.name, 'arzt', 'sprechstunde', ctx.d0, ctx.d4, false, ctx.now));
  }

  // MFA → Labor (erste MFA ganze Woche)
  const mfas = byRole('mfa');
  if (mfas.length >= 1) {
    slots.push(mkSlot(ctx.scheduleId, ctx.practiceId, mfas[0]!.id, mfas[0]!.name, 'mfa', 'labor', ctx.d0, ctx.d4, false, ctx.now));
  }

  // Empfang → Empfang
  const empfang = byRole('empfang');
  if (empfang.length >= 2) {
    slots.push(mkSlot(ctx.scheduleId, ctx.practiceId, empfang[0]!.id, empfang[0]!.name, 'empfang', 'empfang', ctx.d0, ctx.d2, false, ctx.now));
    slots.push(mkSlot(ctx.scheduleId, ctx.practiceId, empfang[1]!.id, empfang[1]!.name, 'empfang', 'empfang', ctx.d3, ctx.d4, false, ctx.now));
  } else if (empfang.length === 1) {
    slots.push(mkSlot(ctx.scheduleId, ctx.practiceId, empfang[0]!.id, empfang[0]!.name, 'empfang', 'empfang', ctx.d0, ctx.d4, false, ctx.now));
  }

  // Open Shifts (Demo) — 2 unbesetzte Schichten
  slots.push(mkSlot(ctx.scheduleId, ctx.practiceId, null, null, 'mfa', 'empfang', ctx.d3, ctx.d3, false, ctx.now, true));
  slots.push(mkSlot(ctx.scheduleId, ctx.practiceId, null, null, 'arzt', 'sprechstunde', ctx.d4, ctx.d4, false, ctx.now, true));

  // Fallback: Wenn gar keine Slots → alle Staff auf Sprechstunde verteilen
  if (slots.length === 0) {
    for (const s of staff) {
      slots.push(mkSlot(ctx.scheduleId, ctx.practiceId, s.id, s.name, s.role, 'sprechstunde', ctx.d0, ctx.d4, false, ctx.now));
    }
  }

  return slots;
}

// In-Memory-State für Demo (wird bei Session-Reset zurückgesetzt)
const demoScheduleStore: Map<string, WeekSchedule> = new Map();

// Cache für echte Staff-Daten (Demo-Modus)
let cachedRealStaff: ScheduleStaffMember[] | null = null;

/** Setzt den Staff-Cache für Demo-Schedule-Generierung */
export function setDemoStaffCache(staff: ScheduleStaffMember[]): void {
  cachedRealStaff = staff;
  // Invalidiere Demo-Schedules damit sie mit echtem Staff neu generiert werden
  demoScheduleStore.clear();
}

function getDemoSchedule(practiceId: string, weekStart: string): WeekSchedule {
  const key = `${practiceId}:${weekStart}`;
  if (!demoScheduleStore.has(key)) {
    demoScheduleStore.set(key, {
      id: `demo-${weekStart}`,
      practice_id: practiceId,
      week_start: weekStart,
      status: 'draft',
      slots: generateDemoSlots(weekStart, cachedRealStaff ?? undefined),
      created_by: 'demo-user',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  }
  return demoScheduleStore.get(key)!;
}

// ============================================================================
// Internal: Week-Schedule Upsert (FK-Constraint benötigt immer einen Parent)
// ============================================================================

/**
 * Ensure a week_schedule record exists (upsert).
 * Muss vor jedem Slot-Insert aufgerufen werden wegen FK-Constraint.
 * Gibt die schedule-ID zurück oder null bei Demo-Modus.
 */
async function ensureWeekSchedule(
  practiceId: string,
  weekStart: string,
  createdBy: string = 'system',
): Promise<string | null> {
  if (!isSupabaseConfigured()) return null;

  try {
    const { data, error } = await supabase
      .from('week_schedules')
      .upsert(
        {
          practice_id: practiceId,
          week_start: weekStart,
          status: 'draft',
          created_by: createdBy,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'practice_id,week_start' },
      )
      .select('id')
      .single();

    if (!error && data) return data.id as string;
    console.warn('[schedule.service] ensureWeekSchedule upsert failed:', error?.message);
  } catch (err) {
    console.warn('[schedule.service] ensureWeekSchedule error:', err);
  }
  return null;
}

// ============================================================================
// Open-Shift Detection
// ============================================================================

// ============================================================================
// Public API
// ============================================================================

/** Holt historische Schicht-Slots für KI-Musteranalysen (T9) */
export async function getHistoricalSlots(
  practiceId: string,
  weekStart: string,
  weeksBack: number = 4
): Promise<ShiftSlot[]> {
  if (!isSupabaseConfigured()) return [];

  const startObj = new Date(weekStart);
  startObj.setDate(startObj.getDate() - (7 * weeksBack));
  const thresholdDate = startObj.toISOString().split('T')[0]!;

  try {
    const { data, error } = await supabase
      .from('shift_slots')
      .select('*')
      .eq('practice_id', practiceId)
      .gte('start_date', thresholdDate)
      .lt('start_date', weekStart);

    if (error) {
      console.warn('[schedule.service] getHistoricalSlots error:', error.message);
      return [];
    }
    return (data || []) as ShiftSlot[];
  } catch (err) {
    console.warn('[schedule.service] getHistoricalSlots exception:', err);
    return [];
  }
}

/** Wochenplan laden (DB-First, Demo-Fallback) */
export async function getWeekSchedule(
  practiceId: string,
  weekStart: string,
): Promise<WeekSchedule> {
  if (isSupabaseConfigured()) {
    try {
      // Ensure record exists
      await ensureWeekSchedule(practiceId, weekStart);

      const { data, error } = await supabase
        .from('week_schedules')
        .select('*, shift_slots(*)')
        .eq('practice_id', practiceId)
        .eq('week_start', weekStart)
        .maybeSingle();

      if (!error && data) {
        const dbSlots: ShiftSlot[] = ((data.shift_slots as ShiftSlot[]) || []).map(s => ({
          ...s,
          is_open: s.is_open ?? false,
        }));

        return {
          id: data.id,
          practice_id: data.practice_id,
          week_start: data.week_start,
          status: data.status ?? 'draft',
          slots: dbSlots,
          created_by: data.created_by ?? 'system',
          published_at: data.published_at ?? undefined,
          created_at: data.created_at,
          updated_at: data.updated_at,
        };
      }
    } catch (err) {
      console.warn('[schedule.service] getWeekSchedule DB-Fehler, Demo-Fallback:', err);
    }
  }

  return getDemoSchedule(practiceId, weekStart);
}

// TeamRole → StaffRole Mapping (practice_staff ist Single Source of Truth)
const TEAM_ROLE_TO_STAFF_ROLE: Record<string, StaffRole> = {
  doctor: 'arzt',
  owner: 'arzt',       // Praxisinhaber sind i.d.R. Ärzte
  admin: 'mfa',        // Admin = leitende MFA
  mfa: 'mfa',
  reception: 'empfang',
  labor: 'labor',
};

const STAFF_ROLE_COLOR: Record<StaffRole, string> = {
  arzt: 'bg-red-100',
  mfa: 'bg-blue-100',
  empfang: 'bg-yellow-100',
  labor: 'bg-emerald-100',
  vertretung: 'bg-amber-50',
};

/** Verfügbare Mitarbeiter laden — Single Source: practice_staff */
export async function getScheduleStaff(
  practiceId: string,
): Promise<ScheduleStaffMember[]> {
  if (isSupabaseConfigured()) {
    try {
      // Single Source of Truth: practice_staff enthält alle Rollen
      const { data, error } = await supabase
        .from('practice_staff')
        .select('id, first_name, last_name, role, is_active')
        .eq('practice_id', practiceId)
        .eq('is_active', true);

      if (error) {
        console.warn('[schedule.service] getScheduleStaff DB-Fehler:', error.message);
        // DB-Fehler → Demo-Fallback
        return [...DEMO_STAFF];
      }

      // DB erreichbar, aber leer → leere Liste (kein Demo!)
      if (!data || data.length === 0) return [];

      return data.map(s => {
        const role: StaffRole = TEAM_ROLE_TO_STAFF_ROLE[s.role] ?? 'mfa';
        return {
          id: s.id,
          name: `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim() || s.role,
          role,
          color: STAFF_ROLE_COLOR[role],
          is_available: true,
        };
      });
    } catch (err) {
      console.warn('[schedule.service] getScheduleStaff Netzwerk-Fehler, Demo-Fallback:', err);
    }
  }

  // Nur wenn Supabase nicht konfiguriert oder Netzwerk-Fehler
  return [...DEMO_STAFF];
}

/** Neuen Shift-Slot erstellen — UUID, DB-First */
export async function createShiftSlot(
  slot: Omit<ShiftSlot, 'id' | 'created_at' | 'updated_at'>,
): Promise<ShiftSlot> {
  const now = new Date().toISOString();

  if (isSupabaseConfigured()) {
    try {
      const { data, error } = await supabase
        .from('shift_slots')
        .insert({
          schedule_id: slot.schedule_id,
          practice_id: slot.practice_id,
          staff_id: slot.staff_id,
          staff_name: slot.staff_name,
          staff_role: slot.staff_role,
          position_id: slot.position_id,
          start_date: slot.start_date,
          end_date: slot.end_date,
          start_time: slot.start_time || null,
          end_time: slot.end_time || null,
          is_override: slot.is_override,
          is_open: slot.is_open ?? false,
          note: slot.note || null,
        })
        .select()
        .single();

      if (!error && data) return data as ShiftSlot;
      throw new Error(error?.message ?? 'Insert failed');
    } catch (err) {
      console.warn('[schedule.service] createShiftSlot DB-Fehler, In-Memory-Fallback:', err);
    }
  }

  // Demo: In-Memory-Fallback mit UUID
  const newSlot: ShiftSlot = {
    ...slot,
    id: crypto.randomUUID(),
    created_at: now,
    updated_at: now,
  };

  for (const [, s] of demoScheduleStore) {
    if (s.practice_id === slot.practice_id) {
      const wdates = getWeekDates(s.week_start);
      const firstDay = wdates[0];
      const lastDay = wdates[4];
      const slotDate = new Date(slot.start_date);
      if (firstDay && lastDay && slotDate >= firstDay && slotDate <= lastDay) {
        s.slots.push(newSlot);
        s.updated_at = now;
        break;
      }
    }
  }

  return newSlot;
}

/** Shift-Slot verschieben (Position, Tage, Zeiten, Notiz) */
export async function moveShiftSlot(
  slotId: string,
  updates: {
    position_id?: string;
    start_date?: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
    note?: string;
  },
): Promise<ShiftSlot | null> {
  const now = new Date().toISOString();

  if (isSupabaseConfigured()) {
    try {
      const { data, error } = await supabase
        .from('shift_slots')
        .update({ ...updates, updated_at: now })
        .eq('id', slotId)
        .select()
        .single();

      if (!error && data) return data as ShiftSlot;
      console.warn('[schedule.service] moveShiftSlot DB-Fehler:', error?.message);
    } catch (err) {
      console.warn('[schedule.service] moveShiftSlot error, Demo-Fallback:', err);
    }
  }

  // Demo: In-Memory Update
  for (const [, schedule] of demoScheduleStore) {
    const slot = schedule.slots.find(s => s.id === slotId);
    if (slot) {
      if (updates.position_id) slot.position_id = updates.position_id;
      if (updates.start_date) slot.start_date = updates.start_date;
      if (updates.end_date) slot.end_date = updates.end_date;
      if (updates.start_time !== undefined) slot.start_time = updates.start_time;
      if (updates.end_time !== undefined) slot.end_time = updates.end_time;
      if (updates.note !== undefined) slot.note = updates.note;
      slot.updated_at = now;
      return { ...slot };
    }
  }

  return null;
}

/** Shift-Slot löschen */
export async function deleteShiftSlot(slotId: string): Promise<boolean> {
  if (isSupabaseConfigured()) {
    try {
      const { error } = await supabase
        .from('shift_slots')
        .delete()
        .eq('id', slotId);

      if (!error) return true;
      console.warn('[schedule.service] deleteShiftSlot DB-Fehler:', error.message);
    } catch (err) {
      console.warn('[schedule.service] deleteShiftSlot error, Demo-Fallback:', err);
    }
  }

  // Demo: In-Memory Delete
  for (const [, schedule] of demoScheduleStore) {
    const idx = schedule.slots.findIndex(s => s.id === slotId);
    if (idx >= 0) {
      schedule.slots.splice(idx, 1);
      return true;
    }
  }

  return false;
}

/** Wochenplan veröffentlichen */
export async function publishSchedule(scheduleId: string): Promise<boolean> {
  const now = new Date().toISOString();

  if (isSupabaseConfigured()) {
    try {
      const { error } = await supabase
        .from('week_schedules')
        .update({ status: 'published', published_at: now, updated_at: now })
        .eq('id', scheduleId);

      if (!error) return true;
    } catch (err) {
      console.warn('[schedule.service] publishSchedule DB-Fehler, Demo-Fallback:', err);
    }
  }

  // Demo: In-Memory
  for (const [, schedule] of demoScheduleStore) {
    if (schedule.id === scheduleId) {
      schedule.status = 'published';
      schedule.published_at = now;
      return true;
    }
  }

  return false;
}

// ============================================================================
// Konflikterkennung — Double-Booking + Duplikat-Prüfung
// ============================================================================

export interface ShiftConflict {
  staffId: string;
  staffName: string;
  date: string;
  positionA: string;
  positionB: string;
  type: 'double-booking' | 'duplicate';
}

/**
 * Prüft ob ein Mitarbeiter am gleichen Tag bereits eingeplant ist.
 * Erkennt sowohl Double-Booking (andere Position) als auch Duplikate (gleiche Position).
 */
export function checkConflicts(
  existingSlots: ShiftSlot[],
  staffId: string,
  staffName: string,
  targetDate: string,
  targetPositionId: string,
  excludeSlotId?: string,
): ShiftConflict | null {
  for (const slot of existingSlots) {
    if (excludeSlotId && slot.id === excludeSlotId) continue;
    if (slot.staff_id !== staffId) continue;
    // Datumsbereich überlappend?
    if (targetDate >= slot.start_date && targetDate <= slot.end_date) {
      if (slot.position_id !== targetPositionId) {
        return {
          staffId,
          staffName,
          date: targetDate,
          positionA: slot.position_id,
          positionB: targetPositionId,
          type: 'double-booking',
        };
      }
      // Gleiche Position = Duplikat
      return {
        staffId,
        staffName,
        date: targetDate,
        positionA: slot.position_id,
        positionB: targetPositionId,
        type: 'duplicate',
      };
    }
  }
  return null;
}

// ============================================================================
// Woche zurkücksetzen / Entwurf
// ============================================================================

/** Wochenplan zurück auf Entwurf setzen */
export async function unpublishSchedule(scheduleId: string): Promise<boolean> {
  const now = new Date().toISOString();

  if (isSupabaseConfigured()) {
    try {
      const { error } = await supabase
        .from('week_schedules')
        .update({ status: 'draft', published_at: null, updated_at: now })
        .eq('id', scheduleId);

      if (!error) return true;
      console.warn('[schedule.service] unpublishSchedule DB-Fehler:', error.message);
    } catch (err) {
      console.warn('[schedule.service] unpublishSchedule error:', err);
    }
  }

  for (const [, schedule] of demoScheduleStore) {
    if (schedule.id === scheduleId) {
      schedule.status = 'draft';
      schedule.published_at = undefined;
      return true;
    }
  }

  return false;
}

// ============================================================================
// Woche kopieren
// ============================================================================

/** Alle Slots einer Quell-Woche in eine Ziel-Woche kopieren */
export async function copyWeekSchedule(
  practiceId: string,
  sourceWeekStart: string,
  targetWeekStart: string,
): Promise<number> {
  const source = await getWeekSchedule(practiceId, sourceWeekStart);
  if (!source || source.slots.length === 0) return 0;

  // Datums-Differenz berechnen
  const sourceMon = new Date(sourceWeekStart + 'T00:00:00');
  const targetMon = new Date(targetWeekStart + 'T00:00:00');
  const dayDiff = Math.round((targetMon.getTime() - sourceMon.getTime()) / 86_400_000);

  function shiftDate(iso: string): string {
    const d = new Date(iso + 'T00:00:00');
    d.setDate(d.getDate() + dayDiff);
    return d.toISOString().slice(0, 10);
  }

  // Ziel-Schedule sicherstellen
  const target = await getWeekSchedule(practiceId, targetWeekStart);

  let count = 0;
  for (const slot of source.slots) {
    await createShiftSlot({
      schedule_id: target.id,
      practice_id: practiceId,
      staff_id: slot.staff_id,
      staff_name: slot.staff_name,
      staff_role: slot.staff_role,
      position_id: slot.position_id,
      start_date: shiftDate(slot.start_date),
      end_date: shiftDate(slot.end_date),
      start_time: slot.start_time,
      end_time: slot.end_time,
      is_override: slot.is_override,
      note: slot.note,
    });
    count++;
  }

  return count;
}

// ============================================================================
// Stunden-Berechnung
// ============================================================================

export interface StaffHoursSummary {
  staffId: string;
  staffName: string;
  totalHours: number;
  days: number;
}

/** Berechne Stunden-Summary pro Mitarbeiter für eine Woche */
export function calculateStaffHours(slots: ShiftSlot[]): StaffHoursSummary[] {
  const map = new Map<string, StaffHoursSummary>();

  for (const slot of slots) {
    const existing = map.get(slot.staff_id) || {
      staffId: slot.staff_id,
      staffName: slot.staff_name,
      totalHours: 0,
      days: 0,
    };

    // Berechne Tage
    const start = new Date(slot.start_date + 'T00:00:00');
    const end = new Date(slot.end_date + 'T00:00:00');
    const dayCount = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1);
    existing.days += dayCount;

    // Berechne Stunden pro Tag (aus start_time/end_time)
    if (slot.start_time && slot.end_time) {
      const [sh, sm] = slot.start_time.split(':').map(Number);
      const [eh, em] = slot.end_time.split(':').map(Number);
      const hoursPerDay = ((eh ?? 0) * 60 + (em ?? 0) - (sh ?? 0) * 60 - (sm ?? 0)) / 60;
      existing.totalHours += hoursPerDay * dayCount;
    } else {
      // Default: 8h pro Tag wenn keine Zeiten gesetzt
      existing.totalHours += 8 * dayCount;
    }

    map.set(slot.staff_id, existing);
  }

  return Array.from(map.values()).sort((a, b) => b.totalHours - a.totalHours);
}

// ============================================================================
// Schicht-Templates
// ============================================================================

export interface ShiftTemplate {
  id: string;
  label: string;
  startTime: string;
  endTime: string;
  icon: string;
}

export const SHIFT_TEMPLATES: ShiftTemplate[] = [
  { id: 'frueh',   label: 'Frühdienst',  startTime: '07:00', endTime: '13:00', icon: '🌅' },
  { id: 'spaet',   label: 'Spätdienst',  startTime: '13:00', endTime: '19:00', icon: '🌇' },
  { id: 'ganztag', label: 'Ganztag',     startTime: '08:00', endTime: '17:00', icon: '☀️' },
  { id: 'kurz',    label: 'Halbtag',     startTime: '08:00', endTime: '12:00', icon: '⏱️' },
];

// ============================================================================
// Realtime-Subscription
// ============================================================================

export type ScheduleChangeCallback = (
  event: 'INSERT' | 'UPDATE' | 'DELETE',
  slot: ShiftSlot | { id: string },
) => void;

/**
 * Echtzeit-Subscription für Schicht-Änderungen.
 * Gibt unsubscribe-Funktion zurück.
 */
export function subscribeScheduleChanges(
  scheduleId: string,
  callback: ScheduleChangeCallback,
): () => void {
  if (!isSupabaseConfigured()) return () => {};

  const channel = supabase
    .channel(`schedule-${scheduleId}`)
    .on(
      'postgres_changes' as 'system',
      {
        event: '*',
        schema: 'public',
        table: 'shift_slots',
        filter: `schedule_id=eq.${scheduleId}`,
      } as Record<string, unknown>,
      (payload: { eventType: string; new?: Record<string, unknown>; old?: Record<string, unknown> }) => {
        const eventType = payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE';
        if (eventType === 'DELETE') {
          callback('DELETE', { id: (payload.old as { id: string })?.id ?? '' });
        } else {
          callback(eventType, payload.new as unknown as ShiftSlot);
        }
      },
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

// ============================================================================
// Positions-Konfigurator — Persistent pro Praxis (Supabase + localStorage-Fallback)
// ============================================================================

const POSITIONS_STORAGE_KEY = 'mediflow:schedule:positions';

/** DB-Row → PositionConfig mapping */
function dbRowToPositionConfig(row: {
  position_id: string;
  label: string;
  required_role: string | null;
  color_bg: string;
  color_text: string;
  color_border: string;
  sort_order: number;
  max_staff: number;
}): PositionConfig {
  return {
    id: row.position_id,
    label: row.label,
    requiredRole: (row.required_role as PositionConfig['requiredRole']) ?? undefined,
    color: { bg: row.color_bg, text: row.color_text, border: row.color_border },
    sortOrder: row.sort_order,
    maxStaff: row.max_staff,
  };
}

/** Positions-Konfiguration laden (Supabase → localStorage-Fallback → Defaults) */
export async function getPositionsConfig(practiceId: string): Promise<PositionConfig[]> {
  // 1. Versuche aus Supabase zu laden
  if (isSupabaseConfigured()) {
    try {
      const { data, error } = await supabase
        .from('position_configs')
        .select('position_id, label, required_role, color_bg, color_text, color_border, sort_order, max_staff')
        .eq('practice_id', practiceId)
        .order('sort_order', { ascending: true });

      if (!error && data && data.length > 0) {
        return data.map(dbRowToPositionConfig);
      }
    } catch {
      // DB nicht erreichbar → Fallback
    }

    // 2. Falls DB leer: localStorage prüfen und ggf. migrieren
    try {
      const raw = localStorage.getItem(`${POSITIONS_STORAGE_KEY}:${practiceId}`);
      if (raw) {
        const parsed = JSON.parse(raw) as PositionConfig[];
        const positions = parsed.map(p => {
          const defaultDef = DEFAULT_POSITIONS.find(dp => dp.id === p.id);
          return { ...p, maxStaff: p.maxStaff ?? defaultDef?.maxStaff ?? 2 };
        });
        // Einmalige Migration: localStorage → Supabase
        const migrated = await savePositionsConfig(practiceId, positions);
        if (migrated) {
          localStorage.removeItem(`${POSITIONS_STORAGE_KEY}:${practiceId}`);
        }
        return positions;
      }
    } catch {
      // Corrupted localStorage → use defaults
    }
  } else {
    // Supabase nicht konfiguriert → localStorage-only (Demo-Modus)
    try {
      const raw = localStorage.getItem(`${POSITIONS_STORAGE_KEY}:${practiceId}`);
      if (raw) {
        const parsed = JSON.parse(raw) as PositionConfig[];
        return parsed.map(p => {
          const defaultDef = DEFAULT_POSITIONS.find(dp => dp.id === p.id);
          return { ...p, maxStaff: p.maxStaff ?? defaultDef?.maxStaff ?? 2 };
        });
      }
    } catch {
      // Corrupted localStorage
    }
  }

  // 3. Fallback: Default-Positionen
  return DEFAULT_POSITIONS.map(p => ({ ...p }));
}

/** Positions-Konfiguration speichern (Supabase UPSERT + Löschung entfernter Positionen) */
export async function savePositionsConfig(practiceId: string, positions: PositionConfig[]): Promise<boolean> {
  if (!isSupabaseConfigured()) {
    // Demo-Modus: localStorage als Fallback
    try {
      localStorage.setItem(
        `${POSITIONS_STORAGE_KEY}:${practiceId}`,
        JSON.stringify(positions),
      );
    } catch {
      console.warn('[schedule.service] Positionen konnten nicht gespeichert werden');
    }
    return false;
  }

  const rows = positions.map(p => ({
    practice_id: practiceId,
    position_id: p.id,
    label: p.label,
    required_role: p.requiredRole ?? null,
    color_bg: p.color.bg,
    color_text: p.color.text,
    color_border: p.color.border,
    sort_order: p.sortOrder,
    max_staff: p.maxStaff,
  }));

  // UPSERT aktuelle Positionen
  const { error: upsertError } = await supabase
    .from('position_configs')
    .upsert(rows, { onConflict: 'practice_id,position_id' });

  if (upsertError) {
    console.warn('[schedule.service] Positionen konnten nicht gespeichert werden:', upsertError.message);
    // localStorage-Fallback damit Daten nicht verloren gehen
    try {
      localStorage.setItem(
        `${POSITIONS_STORAGE_KEY}:${practiceId}`,
        JSON.stringify(positions),
      );
    } catch {
      // silent
    }
    return false;
  }

  // Entfernte Positionen löschen
  const currentIds = positions.map(p => p.id);
  if (currentIds.length > 0) {
    const { error: deleteError } = await supabase
      .from('position_configs')
      .delete()
      .eq('practice_id', practiceId)
      .not('position_id', 'in', `(${currentIds.join(',')})`);

    if (deleteError) {
      console.warn('[schedule.service] Alte Positionen konnten nicht gelöscht werden:', deleteError.message);
      return false;
    }
  }

  return true;
}

// ============================================================================
// Kapazitätsprüfung — Multi-Staff pro Position/Tag
// ============================================================================

/**
 * Prüft ob in einer Zelle (Position × Tag) noch Platz ist.
 * @returns `true` wenn noch Platz, `false` wenn max erreicht
 */
export function checkCellCapacity(
  existingSlots: ShiftSlot[],
  targetDate: string,
  targetPositionId: string,
  maxStaff: number,
): boolean {
  const count = existingSlots.filter(s =>
    s.position_id === targetPositionId &&
    targetDate >= s.start_date && targetDate <= s.end_date,
  ).length;
  return count < maxStaff;
}

// ============================================================================
// Publish & Notify — Dienstplan veröffentlichen + E-Mail an Team
// ============================================================================

/** Staff mit E-Mail-Adressen laden für Benachrichtigung */
export async function getStaffWithEmails(
  practiceId: string,
): Promise<Array<{ staffId: string; name: string; email: string }>> {
  if (!isSupabaseConfigured()) return [];

  try {
    const { data, error } = await supabase
      .from('practice_staff')
      .select('id, first_name, last_name, email, is_active')
      .eq('practice_id', practiceId)
      .eq('is_active', true);

    if (error || !data) return [];

    return data
      .filter(s => s.email)
      .map(s => ({
        staffId: s.id,
        name: `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim() || 'Mitarbeiter',
        email: s.email,
      }));
  } catch {
    return [];
  }
}

/**
 * Dienstplan veröffentlichen UND Team per E-Mail benachrichtigen.
 * Gibt { published, emailsSent, emailErrors } zurück.
 */
export async function publishAndNotifySchedule(
  scheduleId: string,
  practiceId: string,
  weekStart: string,
  options: SchedulePublishOptions,
): Promise<{ published: boolean; emailsSent: number; emailErrors: string[] }> {
  const result = { published: false, emailsSent: 0, emailErrors: [] as string[] };

  // 1. Status in DB setzen
  const published = await publishSchedule(scheduleId);
  result.published = published;
  if (!published) return result;

  // 2. Falls E-Mail gewünscht → Edge Function aufrufen
  if (options.sendEmail && options.recipients.length > 0 && isSupabaseConfigured()) {
    try {
      const { error } = await supabase.functions.invoke('send-schedule-email', {
        body: {
          practiceId,
          weekStart,
          kwNumber: options.kwNumber,
          practiceName: options.practiceName,
          recipients: options.recipients.map(r => ({
            staffId: r.staffId,
            name: r.name,
            email: r.email,
          })),
          slots: (options.slots || []).map(s => {
            // Position-Label: aus DEFAULT_POSITIONS nachschlagen, Fallback auf ID mit Großbuchstabe
            const posConfig = DEFAULT_POSITIONS.find(p => p.id === s.position_id);
            const label = posConfig?.label ?? s.position_id.charAt(0).toUpperCase() + s.position_id.slice(1);
            return {
              staff_name: s.staff_name,
              staff_id: s.staff_id,
              position_label: label,
              start_date: s.start_date,
              end_date: s.end_date,
              start_time: s.start_time,
              end_time: s.end_time,
            };
          }),
        },
      });

      if (error) {
        console.warn('[schedule.service] E-Mail-Versand fehlgeschlagen:', error);
        result.emailErrors.push('E-Mail-Versand fehlgeschlagen. Dienstplan wurde trotzdem veröffentlicht.');
      } else {
        result.emailsSent = options.recipients.length;
      }
    } catch (err) {
      console.warn('[schedule.service] E-Mail-Versand Exception:', err);
      result.emailErrors.push('E-Mail-Dienst nicht erreichbar. Dienstplan wurde trotzdem veröffentlicht.');
    }
  }

  return result;
}

// ============================================================================
// MVP Parity Features (Marktführer-Standard)
// ============================================================================

// ── Availability ─────────────────────────────────────────────────────────────

export async function getStaffAvailability(
  practiceId: string,
  weekStart: string,
): Promise<StaffAvailability[]> {
  if (!isSupabaseConfigured()) return [];

  const weekEnd = new Date(new Date(weekStart).getTime() + 4 * 86400000)
    .toISOString().slice(0, 10);

  const { data, error } = await supabase
    .from('staff_availability')
    .select('*')
    .eq('practice_id', practiceId)
    .gte('date', weekStart)
    .lte('date', weekEnd)
    .order('date');

  if (error) {
    console.error('[Schedule] Availability laden fehlgeschlagen:', error.message);
    return [];
  }
  return (data ?? []) as StaffAvailability[];
}

export async function setStaffAvailability(
  input: Omit<StaffAvailability, 'id' | 'created_at' | 'updated_at'>,
): Promise<StaffAvailability | null> {
  if (!isSupabaseConfigured()) return null;

  const { data, error } = await supabase
    .from('staff_availability')
    .upsert(input, { onConflict: 'practice_id,staff_id,date' })
    .select()
    .single();

  if (error) {
    console.error('[Schedule] Availability setzen fehlgeschlagen:', error.message);
    return null;
  }
  return data as StaffAvailability;
}

// ── Open Shifts ──────────────────────────────────────────────────────────────

export async function createOpenShift(
  practiceId: string,
  scheduleId: string,
  positionId: string,
  date: string,
  startTime?: string,
  endTime?: string,
  note?: string,
): Promise<ShiftSlot | null> {
  if (!isSupabaseConfigured()) return null;

  const { data, error } = await supabase
    .from('shift_slots')
    .insert({
      schedule_id: scheduleId,
      practice_id: practiceId,
      staff_id: null,
      staff_name: null,
      staff_role: 'mfa',
      position_id: positionId,
      start_date: date,
      end_date: date,
      start_time: startTime || null,
      end_time: endTime || null,
      is_open: true,
      is_override: false,
      note: note || 'Offene Schicht — bitte beanspruchen',
    })
    .select()
    .single();

  if (error) {
    console.error('[Schedule] Open Shift erstellen fehlgeschlagen:', error.message);
    return null;
  }
  return data as ShiftSlot;
}

export async function claimOpenShift(
  slotId: string,
  staffId: string,
  staffName: string,
  staffRole: StaffRole,
): Promise<boolean> {
  if (!isSupabaseConfigured()) return false;

  const { error } = await supabase
    .from('shift_slots')
    .update({
      staff_id: staffId,
      staff_name: staffName,
      staff_role: staffRole,
      is_open: false,
      note: null,
    })
    .eq('id', slotId)
    .eq('is_open', true);

  if (error) {
    console.error('[Schedule] Open Shift beanspruchen fehlgeschlagen:', error.message);
    return false;
  }
  return true;
}

// ── ArbZG Compliance ─────────────────────────────────────────────────────────

/** Liefert Demo-ArbZG-Warnungen für den ersten und vierten Mitarbeiter in der Liste */
function getDemoArbZGViolations(
  staffId: string,
  weekStart: string,
): ArbZGViolation[] {
  const allStaff = cachedRealStaff && cachedRealStaff.length > 0 ? cachedRealStaff : DEMO_STAFF;
  const firstId = allStaff[0]?.id;
  const fourthId = allStaff[3]?.id;

  if (staffId === firstId) {
    return [
      { violation_type: 'overtime_warning', severity: 'warning', message: 'Überstunden: 44h geplant (Soll: 40h)', affected_date: weekStart },
    ];
  }
  if (fourthId && staffId === fourthId) {
    return [
      { violation_type: 'max_consecutive_days', severity: 'error', message: '7 aufeinanderfolgende Arbeitstage — max. 6 erlaubt (§9 ArbZG)', affected_date: weekStart },
    ];
  }
  return [];
}

function addDaysToISODate(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00`);
  date.setDate(date.getDate() + days);
  return formatLocalDate(date);
}

function getShiftHoursPerDay(slot: Pick<ShiftSlot, 'start_time' | 'end_time'>): number {
  if (slot.start_time && slot.end_time) {
    const [startHour = 8, startMinute = 0] = slot.start_time.split(':').map(Number);
    const [endHour = 17, endMinute = 0] = slot.end_time.split(':').map(Number);
    const minutes = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
    return minutes > 0 ? minutes / 60 : 8;
  }
  return 8;
}

function getOverlappingShiftDates(
  slot: Pick<ShiftSlot, 'start_date' | 'end_date'>,
  rangeStart: string,
  rangeEnd: string,
): string[] {
  const overlapStart = slot.start_date > rangeStart ? slot.start_date : rangeStart;
  const overlapEnd = slot.end_date < rangeEnd ? slot.end_date : rangeEnd;

  if (overlapStart > overlapEnd) {
    return [];
  }

  const dates: string[] = [];
  let cursor = new Date(`${overlapStart}T00:00:00`);
  const end = new Date(`${overlapEnd}T00:00:00`);

  while (cursor <= end) {
    dates.push(formatLocalDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
}

async function getShiftSlotsForRange(
  practiceId: string,
  rangeStart: string,
  rangeEnd: string,
  staffId?: string,
): Promise<ShiftSlot[]> {
  let query = supabase
    .from('shift_slots')
    .select('*')
    .eq('practice_id', practiceId)
    .lte('start_date', rangeEnd)
    .gte('end_date', rangeStart)
    .order('start_date', { ascending: true });

  if (staffId) {
    query = query.eq('staff_id', staffId);
  }

  const { data, error } = await query;
  if (error) {
    console.warn('[Schedule] Shift-Slots für Fallback-Berechnung konnten nicht geladen werden:', error.message);
    return [];
  }

  return (data ?? []) as ShiftSlot[];
}

async function getHoursSummaryFallback(
  practiceId: string,
  weekStart: string,
): Promise<HoursSummaryRow[]> {
  const weekEnd = addDaysToISODate(weekStart, 4);
  const [staffList, contracts, slots] = await Promise.all([
    getScheduleStaff(practiceId),
    getContractHours(practiceId),
    getShiftSlotsForRange(practiceId, weekStart, weekEnd),
  ]);

  const contractMap = new Map(contracts.map((contract) => [contract.staff_id, contract]));
  const rows = new Map<string, HoursSummaryRow>();

  for (const staffMember of staffList) {
    const contract = contractMap.get(staffMember.id);
    const contractHours = contract?.weekly_hours ?? 40;
    rows.set(staffMember.id, {
      staff_id: staffMember.id,
      staff_name: staffMember.name,
      staff_role: staffMember.role,
      planned_hours: 0,
      contract_hours: contractHours,
      diff_hours: -contractHours,
      shift_count: 0,
    });
  }

  for (const slot of slots) {
    if (!slot.staff_id) continue;

    const overlappingDates = getOverlappingShiftDates(slot, weekStart, weekEnd);
    if (overlappingDates.length === 0) continue;

    const existing = rows.get(slot.staff_id) ?? {
      staff_id: slot.staff_id,
      staff_name: slot.staff_name,
      staff_role: slot.staff_role,
      planned_hours: 0,
      contract_hours: contractMap.get(slot.staff_id)?.weekly_hours ?? 40,
      diff_hours: 0,
      shift_count: 0,
    };

    existing.planned_hours += getShiftHoursPerDay(slot) * overlappingDates.length;
    existing.shift_count += 1;
    existing.diff_hours = existing.planned_hours - existing.contract_hours;
    rows.set(slot.staff_id, existing);
  }

  return Array.from(rows.values()).sort((left, right) => left.staff_name.localeCompare(right.staff_name, 'de'));
}

async function getArbZGComplianceFallback(
  practiceId: string,
  staffId: string,
  weekStart: string,
): Promise<ArbZGViolation[]> {
  const rangeStart = addDaysToISODate(weekStart, -7);
  const weekEnd = addDaysToISODate(weekStart, 6);
  const rangeEnd = addDaysToISODate(weekEnd, 1);
  const slots = await getShiftSlotsForRange(practiceId, rangeStart, rangeEnd, staffId);
  if (slots.length === 0) {
    return [];
  }

  const dailyHours = new Map<string, number>();
  let weeklyHours = 0;

  for (const slot of slots) {
    if (!slot.staff_id) continue;
    const dates = getOverlappingShiftDates(slot, rangeStart, rangeEnd);
    if (dates.length === 0) continue;

    const hoursPerDay = getShiftHoursPerDay(slot);
    for (const date of dates) {
      dailyHours.set(date, (dailyHours.get(date) ?? 0) + hoursPerDay);
      if (date >= weekStart && date <= weekEnd) {
        weeklyHours += hoursPerDay;
      }
    }
  }

  const violations: ArbZGViolation[] = [];

  if (weeklyHours > 48) {
    violations.push({
      violation_type: 'max_weekly_hours',
      severity: 'error',
      message: `Wöchentliche Arbeitszeit ${Math.round(weeklyHours * 10) / 10}h überschreitet 48h-Grenze (§3 ArbZG)`,
      affected_date: weekStart,
    });
  } else if (weeklyHours > 40) {
    violations.push({
      violation_type: 'overtime_warning',
      severity: 'warning',
      message: `Überstunden: ${Math.round(weeklyHours * 10) / 10}h geplant (Soll: 40h)`,
      affected_date: weekStart,
    });
  }

  for (const [date, hours] of dailyHours.entries()) {
    if (date < weekStart || date > weekEnd) continue;
    if (hours > 10) {
      violations.push({
        violation_type: 'max_daily_hours',
        severity: 'error',
        message: `Tägliche Arbeitszeit ${Math.round(hours * 10) / 10}h überschreitet 10h-Grenze (§3 ArbZG)`,
        affected_date: date,
      });
    }
  }

  const workDates = Array.from(dailyHours.keys()).sort();
  let consecutiveDays = 0;
  let previousDate: string | null = null;

  for (const workDate of workDates) {
    if (previousDate) {
      const expectedNext = addDaysToISODate(previousDate, 1);
      consecutiveDays = workDate === expectedNext ? consecutiveDays + 1 : 1;
    } else {
      consecutiveDays = 1;
    }

    if (consecutiveDays > 6) {
      violations.push({
        violation_type: 'max_consecutive_days',
        severity: 'error',
        message: `${consecutiveDays} aufeinanderfolgende Arbeitstage — max. 6 erlaubt (§9 ArbZG)`,
        affected_date: workDate,
      });
      break;
    }

    previousDate = workDate;
  }

  return violations;
}

export async function checkArbZGCompliance(
  practiceId: string,
  staffId: string,
  weekStart: string,
): Promise<ArbZGViolation[]> {
  if (!isSupabaseConfigured()) {
    return getDemoArbZGViolations(staffId, weekStart);
  }

  if (arbzgRpcUnavailable) {
    const fallbackViolations = await getArbZGComplianceFallback(practiceId, staffId, weekStart);
    return fallbackViolations.length > 0 ? fallbackViolations : getDemoArbZGViolations(staffId, weekStart);
  }

  const { data, error } = await supabase
    .rpc('check_arbzg_compliance', {
      p_practice_id: practiceId,
      p_staff_id: staffId,
      p_week_start: weekStart,
    });

  if (error) {
    if (isNonRecoverableScheduleRpcError(error)) {
      arbzgRpcUnavailable = true;
    }
    console.warn('[Schedule] ArbZG RPC nicht verfügbar, nutze lokale Fallback-Berechnung:', error.message);
    const fallbackViolations = await getArbZGComplianceFallback(practiceId, staffId, weekStart);
    if (fallbackViolations.length > 0) {
      return fallbackViolations;
    }
    return getDemoArbZGViolations(staffId, weekStart);
  }
  return (data ?? []) as ArbZGViolation[];
}

export async function checkAllStaffArbZG(
  practiceId: string,
  weekStart: string,
  staffIds: string[],
): Promise<Map<string, ArbZGViolation[]>> {
  const results = new Map<string, ArbZGViolation[]>();

  await Promise.all(
    staffIds.map(async (staffId) => {
      const violations = await checkArbZGCompliance(practiceId, staffId, weekStart);
      if (violations.length > 0) {
        results.set(staffId, violations);
      }
    }),
  );

  return results;
}

// ── Hours Summary (Soll/Ist) ─────────────────────────────────────────────────

/** Liefert Demo-Stundendaten für die ersten 5 Mitarbeiter */
function getDemoHoursSummary(): HoursSummaryRow[] {
  const demoStaff = cachedRealStaff && cachedRealStaff.length > 0 ? cachedRealStaff : DEMO_STAFF;
  return demoStaff.slice(0, 5).map((s: ScheduleStaffMember, i: number) => ({
    staff_id: s.id,
    staff_name: s.name,
    staff_role: s.role,
    planned_hours: [42, 38.5, 40, 44, 36][i] ?? 40,
    contract_hours: 40,
    diff_hours: [2, -1.5, 0, 4, -4][i] ?? 0,
    shift_count: [5, 4, 5, 6, 4][i] ?? 5,
  }));
}

export async function getHoursSummary(
  practiceId: string,
  weekStart: string,
): Promise<HoursSummaryRow[]> {
  if (!isSupabaseConfigured()) {
    return getDemoHoursSummary();
  }

  if (hoursSummaryRpcUnavailable) {
    const fallbackSummary = await getHoursSummaryFallback(practiceId, weekStart);
    return fallbackSummary.length > 0 ? fallbackSummary : getDemoHoursSummary();
  }

  const { data, error } = await supabase
    .rpc('get_schedule_hours_summary', {
      p_practice_id: practiceId,
      p_week_start: weekStart,
    });

  if (error) {
    if (isNonRecoverableScheduleRpcError(error)) {
      hoursSummaryRpcUnavailable = true;
    }
    console.warn('[Schedule] Stunden-RPC nicht verfügbar, nutze lokale Fallback-Berechnung:', error.message);
    const fallbackSummary = await getHoursSummaryFallback(practiceId, weekStart);
    return fallbackSummary.length > 0 ? fallbackSummary : getDemoHoursSummary();
  }
  return (data ?? []) as HoursSummaryRow[];
}

// ── Swap Requests ────────────────────────────────────────────────────────────

export async function getSwapRequests(
  practiceId: string,
): Promise<ScheduleSwapRequest[]> {
  if (!isSupabaseConfigured()) return [];

  const { data, error } = await supabase
    .from('schedule_swap_requests')
    .select('*, requester_slot:shift_slots!requester_slot_id(*), target_slot:shift_slots!target_slot_id(*)')
    .eq('practice_id', practiceId)
    .in('status', ['pending', 'accepted'])
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[Schedule] Swap Requests laden fehlgeschlagen:', error.message);
    return [];
  }
  return (data ?? []) as ScheduleSwapRequest[];
}

export async function createSwapRequest(
  input: Omit<ScheduleSwapRequest, 'id' | 'status' | 'approved_by' | 'approved_at' | 'rejection_reason' | 'expires_at' | 'created_at' | 'updated_at'>,
): Promise<ScheduleSwapRequest | null> {
  if (!isSupabaseConfigured()) return null;

  const { data, error } = await supabase
    .from('schedule_swap_requests')
    .insert({ ...input, status: 'pending' })
    .select()
    .single();

  if (error) {
    console.error('[Schedule] Swap Request erstellen fehlgeschlagen:', error.message);
    return null;
  }
  return data as ScheduleSwapRequest;
}

export async function respondToSwapRequest(
  requestId: string,
  action: 'accepted' | 'rejected' | 'approved',
  rejectionReason?: string,
): Promise<boolean> {
  if (!isSupabaseConfigured()) return false;

  const update: Record<string, unknown> = { status: action };
  if (action === 'rejected' && rejectionReason) {
    update.rejection_reason = rejectionReason;
  }
  if (action === 'approved') {
    update.approved_at = new Date().toISOString();
  }

  const { error } = await supabase
    .from('schedule_swap_requests')
    .update(update)
    .eq('id', requestId);

  if (error) {
    console.error('[Schedule] Swap Response fehlgeschlagen:', error.message);
    return false;
  }
  return true;
}

// ── Contract Hours ───────────────────────────────────────────────────────────

export async function getContractHours(
  practiceId: string,
): Promise<StaffContractHours[]> {
  if (!isSupabaseConfigured()) return [];

  const { data, error } = await supabase
    .from('staff_contract_hours')
    .select('*')
    .eq('practice_id', practiceId)
    .is('valid_until', null)
    .order('staff_name');

  if (error) {
    console.error('[Schedule] Vertragsstunden laden fehlgeschlagen:', error.message);
    return [];
  }
  return (data ?? []) as StaffContractHours[];
}

export async function setContractHours(
  practiceId: string,
  staffId: string,
  staffName: string,
  weeklyHours: number,
): Promise<boolean> {
  if (!isSupabaseConfigured()) return false;

  const { error } = await supabase
    .from('staff_contract_hours')
    .upsert(
      {
        practice_id: practiceId,
        staff_id: staffId,
        staff_name: staffName,
        weekly_hours: weeklyHours,
        valid_from: new Date().toISOString().slice(0, 10),
      },
      { onConflict: 'practice_id,staff_id,valid_from' },
    );

  if (error) {
    console.error('[Schedule] Vertragsstunden setzen fehlgeschlagen:', error.message);
    return false;
  }
  return true;
}
